import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import {
  EMPTY_STATE_BREAKPOINT_PX,
  EMPTY_STATE_IMAGE_SIZE_PX,
  EMPTY_STATE_SIZE_CLASSES,
  emptyStateSizeFromWidth,
  EmptyStateSize,
  EmptyStateVisualSize,
} from '@shared/constants/empty-state.constants';

export type { EmptyStateSize, EmptyStateVisualSize };

/**
 * Canonical empty / no-data placeholder. Fixed structure, every slot optional:
 * image → title → description → projected action (button etc.).
 *
 *   <app-empty-state
 *     [image]="EMPTY_STATE_IMAGE"
 *     title="Nessuna sessione"
 *     description="Aggiungi la prima sessione…">
 *     <p-button label="Nuova sessione" (onClick)="add()" />
 *   </app-empty-state>
 *
 * Use `size="responsive"` in list tables (small < 1440, medium 1440–1920, large > 1920).
 * `small` / `medium` / `large` are fixed — they never follow the viewport.
 */
@Component({
  selector: 'app-empty-state',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div [class]="rootClass()">
      <div [class]="bodyClass()">
        @if (image()) {
          <img
            class="max-w-full object-contain shrink-0"
            [style.width.px]="imageSize().width"
            [style.height.px]="imageSize().height"
            [src]="image()!"
            alt=""
            aria-hidden="true" />
        } @else if (icon()) {
          <i [class]="iconClass()" aria-hidden="true"></i>
        }

        @if (title() || description()) {
          <div [class]="textClass()">
            @if (title()) {
              <h3 [class]="titleClass()">{{ title() }}</h3>
            }
            @if (description()) {
              <p [class]="descriptionClass()">{{ description() }}</p>
            }
          </div>
        }
      </div>

      <ng-content></ng-content>
    </div>
  `,
})
export class EmptyStateComponent {
  /** Illustration path. When set, overrides `icon`. */
  readonly image = input<string | null>(null);
  /** PrimeIcons class used only when `image` is absent. */
  readonly icon = input<string | null>(null);
  readonly title = input<string | null>(null);
  readonly description = input<string | null>(null);
  /** `responsive` follows viewport; otherwise a fixed Figma size. Default is `large`. */
  readonly size = input<EmptyStateSize>('large');

  private readonly viewportSize = signal<EmptyStateVisualSize>('medium');

  readonly resolvedSize = computed<EmptyStateVisualSize>(() => {
    const size = this.size();
    return size === 'responsive' ? this.viewportSize() : size;
  });

  readonly imageSize = computed(() => EMPTY_STATE_IMAGE_SIZE_PX[this.resolvedSize()]);

  private readonly layout = computed(() => EMPTY_STATE_SIZE_CLASSES[this.resolvedSize()]);

  readonly rootClass = computed(
    () => `flex flex-col items-center justify-center text-center w-full ${this.layout().root}`,
  );
  readonly bodyClass = computed(
    () => `flex flex-col items-center ${this.layout().body}`,
  );
  readonly iconClass = computed(
    () => `pi ${this.icon()} text-muted-color opacity-50 shrink-0 ${this.layout().icon}`,
  );
  readonly textClass = computed(
    () => `flex flex-col items-center ${this.layout().text}`,
  );
  readonly titleClass = computed(
    () => `m-0 font-semibold text-surface-900 dark:text-surface-0 ${this.layout().title}`,
  );
  readonly descriptionClass = computed(
    () => `m-0 text-muted-color ${this.layout().description}`,
  );

  /** Viewport listeners exist only while `size` is `responsive`. */
  private readonly viewportEffect = effect((onCleanup) => {
    if (this.size() !== 'responsive' || typeof window === 'undefined') {
      return;
    }

    const medium = window.matchMedia(`(min-width: ${EMPTY_STATE_BREAKPOINT_PX.mediumMin}px)`);
    const large = window.matchMedia(`(min-width: ${EMPTY_STATE_BREAKPOINT_PX.largeMin}px)`);
    const sync = (): void => {
      this.viewportSize.set(emptyStateSizeFromWidth(window.innerWidth));
    };
    sync();
    medium.addEventListener('change', sync);
    large.addEventListener('change', sync);
    onCleanup(() => {
      medium.removeEventListener('change', sync);
      large.removeEventListener('change', sync);
    });
  });
}
